/**
 * autoStatus.js — Auto-transition engine for job statuses
 *
 * Pure function: given a job + invoices + current time, returns the
 * suggested status (or null if no change needed).
 *
 * Auto-transitions:
 *   quoted      → confirmed        when deposit invoice is paid
 *   confirmed   → shooting         during shoot time → EOD on a shoot day
 *   shooting    → post_production  day after a shoot day (with or without future shoots)
 *   post_production → shooting     on next shoot day (shoot time → EOD)
 *   delivered   → invoiced         when balance invoice exists
 *   late-stage  → paid             when fully billed (invoiced ≥ revenue) and all invoices are paid (from partial_delivery/delivered/invoiced)
 *   paid        → archived         3 business days after paid_at
 */

import { isValidTransition } from './statusTransitions.js';

/**
 * Compute the auto-suggested status for a job.
 * @param {object} job — full job object (camelCase fields)
 * @param {object[]} invoices — invoices for this job
 * @param {Date} [now] — current time (default: new Date())
 * @returns {string|null} — new status, or null if no change
 */
export function computeAutoStatus(job, invoices = [], now = new Date()) {
  const status = job.status;
  if (!status || status === 'void' || status === 'archived') return null;

  const todayStr = toDateStr(now);

  // ── quoted → confirmed: deposit invoice paid ──
  if (status === 'quoted') {
    const depositPaid = invoices.some(
      (inv) => inv.invoiceType === 'deposit' && inv.status === 'paid'
    );
    if (depositPaid) return 'confirmed';
  }

  // ── Staycation (pure post, no shoot date) → post_production on confirm ──
  // These jobs never have a shoot day, so the confirmed→shooting→post_production
  // path below would stall them at 'confirmed'. Skip straight to post.
  if (status === 'confirmed'
      && job.workflowType === 'staycation'
      && !job.shootDate
      && !(Array.isArray(job.shootDates) && job.shootDates.length)) {
    return 'post_production';
  }

  // ── shooting / post_production → confirmed: every shoot is still ahead ──
  // A shoot date moved forward after the engine advanced (e.g. a same-day date
  // corrected to tomorrow) leaves an unshot job in shooting, and the
  // shooting→post_production rule below then files it as post. Requires at
  // least one shoot date, so shootless Staycation jobs keep their skip.
  if ((status === 'shooting' || status === 'post_production') && allShootsInFuture(job, todayStr)) {
    return 'confirmed';
  }

  // ── confirmed / post_production → shooting: on a shoot day, during shoot time → EOD ──
  if (status === 'confirmed' || status === 'post_production') {
    if (isShootDay(job, todayStr)) {
      const shootTime = getEarliestShootTime(job, todayStr);
      if (shootTime) {
        const [hh, mm] = shootTime.split(':').map(Number);
        const shootStart = new Date(now);
        shootStart.setHours(hh, mm, 0, 0);
        if (now >= shootStart) return 'shooting';
      } else {
        // No specific time — if it's a shoot day, auto-advance
        return 'shooting';
      }
    }
  }

  // ── shooting → post_production: not a shoot day (gap day or after last shoot) ──
  if (status === 'shooting') {
    if (!isShootDay(job, todayStr)) {
      return 'post_production';
    }
  }

  // ── delivered / partial_delivery → invoiced: balance or full invoice sent ──
  // For jobs that skip the deposit, the post-shoot invoice is a 'full' invoice
  // rather than 'balance' — both should advance the status.
  if (status === 'delivered' || status === 'partial_delivery') {
    const finalSent = invoices.some(
      (inv) => (inv.invoiceType === 'balance' || inv.invoiceType === 'full')
        && inv.status !== 'void'
        && (inv.sentAt || ['sent', 'partial', 'paid'].includes(inv.status))
    );
    if (finalSent) return 'invoiced';
  }

  // ── any late-stage → paid: fully billed and all non-void invoices are paid ──
  if (['invoiced', 'delivered', 'partial_delivery'].includes(status)) {
    // 'extra' invoices are additional charges ON TOP of revenue (e.g. extra
    // Staycation work) — they must not count toward covering the project total
    // or a large extra could flip the job to 'paid' before the balance is paid.
    const coverageInvoices = invoices.filter((inv) => inv.status !== 'void' && inv.invoiceType !== 'extra');
    const totalInvoiced = coverageInvoices.reduce((s, inv) => s + (parseFloat(inv.amount) || 0), 0);
    const jobRev = parseFloat(job.revenue) || 0;
    const fullyBilled = totalInvoiced >= jobRev && jobRev > 0;
    if (coverageInvoices.length > 0 && fullyBilled && coverageInvoices.every((inv) => inv.status === 'paid')) {
      return 'paid';
    }
  }

  // ── paid → archived: 3 business days after paid_at ──
  if (status === 'paid' && job.paidAt) {
    const paidDate = new Date(job.paidAt);
    const archiveDate = addBusinessDays(paidDate, 3);
    if (now >= archiveDate) return 'archived';
  }

  return null;
}

// Backward moves the engine may make that the manual flow map (forward-only)
// rejects: the next-shoot-day return to shooting, and the unshot-job return to
// confirmed. Everything else still has to be a legal forward transition.
const AUTO_REVERSALS = {
  shooting: ['confirmed'],
  post_production: ['shooting', 'confirmed'],
};

/**
 * Apply auto-status if valid. Returns the new status or null.
 */
export function getAutoStatusIfValid(job, invoices = [], now = new Date()) {
  const suggested = computeAutoStatus(job, invoices, now);
  if (!suggested) return null;
  if (isValidTransition(job.status, suggested)
      || (AUTO_REVERSALS[job.status] || []).includes(suggested)) {
    return suggested;
  }
  return null;
}

// ── Helpers ──────────────────────────────────────────────────────────

function toDateStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function isShootDay(job, dateStr) {
  const dates = Array.isArray(job.shootDates) && job.shootDates.length > 0
    ? job.shootDates
    : (job.shootDate ? [job.shootDate] : []);
  return dates.some((d) => {
    const ds = typeof d === 'string' ? d.split('T')[0] : '';
    return ds === dateStr;
  });
}

function allShootsInFuture(job, todayStr) {
  const dates = Array.isArray(job.shootDates) && job.shootDates.length > 0
    ? job.shootDates
    : (job.shootDate ? [job.shootDate] : []);
  const days = dates
    .map((d) => (typeof d === 'string' ? d.split('T')[0] : (d instanceof Date ? toDateStr(d) : '')))
    .filter(Boolean);
  return days.length > 0 && days.every((d) => d > todayStr);
}

function getEarliestShootTime(job, dateStr) {
  const snap = job.calcSnapshot;
  if (!snap) return null;
  const dlLines = snap.deliverableLines || [];
  const times = dlLines
    .filter((dl) => dl.shootDate && dl.shootDate.split('T')[0] === dateStr && dl.shootTime)
    .map((dl) => dl.shootTime)
    .sort();
  return times[0] || null;
}

function addBusinessDays(date, days) {
  const result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const dow = result.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return result;
}

/**
 * Check if a job in post_production has upcoming shoot dates.
 * Used for the "Next shoot" badge in the UI.
 */
export function getNextShootDate(job, now = new Date()) {
  const todayStr = toDateStr(now);
  const dates = Array.isArray(job.shootDates) && job.shootDates.length > 0
    ? job.shootDates
    : (job.shootDate ? [job.shootDate] : []);
  const future = dates
    .map((d) => (typeof d === 'string' ? d.split('T')[0] : ''))
    .filter((d) => d > todayStr)
    .sort();
  return future[0] || null;
}
